import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
   Box,
   Typography,
   Paper,
   Button,
   Grid,
   Card,
   CardContent,
   TextField,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   LinearProgress,
   CircularProgress,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   ToggleButton,
   Checkbox,
   CardMedia,
   IconButton,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import { styled } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CollectionsIcon from '@mui/icons-material/Collections';
import ArticleIcon from '@mui/icons-material/Article';
import MapIcon from '@mui/icons-material/Map';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PaletteIcon from '@mui/icons-material/Palette';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Styled Components
const ExportContainer = styled(Paper)(({ theme }) => ({
   padding: theme.spacing(3),
   borderRadius: '16px',
   background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
   border: `1px solid ${theme.palette.divider}`,
   boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
}));

const TemplateCard = styled(Card)<{ selected: boolean }>(({ theme, selected }) => ({
   cursor: 'pointer',
   transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
   border: selected ? `3px solid ${theme.palette.primary.main}` : '1px solid transparent',
   transform: selected ? 'scale(1.05)' : 'scale(1)',
   '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
   },
}));

const PreviewCanvas = styled(Box)(({ theme }) => ({
   minHeight: '600px',
   backgroundColor: '#f8f9fa',
   border: `2px dashed ${theme.palette.divider}`,
   borderRadius: '8px',
   padding: '20px',
   overflow: 'auto',
   display: 'flex',
   justifyContent: 'center',
   alignItems: 'flex-start',
}));

// Interfaces
interface Photo {
   id: string;
   url: string;
   title?: string;
   createdAt: any;
   location?: { latitude: number; longitude: number };
   camera?: { make: string; model: string };
}

interface MagazineExportProps {
   photos: Photo[];
   albumTitle: string;
   albumDescription?: string;
   photoSelectionSet?: Set<string>;
   onTogglePhotoSelection?: (photoId: string) => void;
   onSharePhoto?: (photo: Photo) => void;
}

type LayoutTemplate = 'classic' | 'story' | 'collage' | 'minimal' | 'travel' | 'portfolio';
type ExportFormat = 'pdf' | 'png' | 'jpg' | 'svg';
type ExportQuality = 'web' | 'email' | 'print' | 'ultra';
type PageSize = 'a4' | 'letter' | 'tabloid' | 'a3' | 'custom';
type PageOrientation = 'portrait' | 'landscape';

// Template configurations
const templates = [
   {
      id: 'classic' as LayoutTemplate,
      name: 'Classic Grid',
      description: 'Clean grid layout with elegant typography',
      icon: ViewModuleIcon,
   },
   {
      id: 'story' as LayoutTemplate,
      name: 'Story Layout',
      description: 'Timeline narrative with flowing design',
      icon: AutoStoriesIcon,
   },
   {
      id: 'collage' as LayoutTemplate,
      name: 'Collage Style',
      description: 'Creative overlapping photo arrangement',
      icon: CollectionsIcon,
   },
   {
      id: 'minimal' as LayoutTemplate,
      name: 'Minimal Modern',
      description: 'Bold typography with lots of white space',
      icon: ArticleIcon,
   },
   {
      id: 'travel' as LayoutTemplate,
      name: 'Travel Magazine',
      description: 'Location-focused with map integration',
      icon: MapIcon,
   },
   {
      id: 'portfolio' as LayoutTemplate,
      name: 'Portfolio',
      description: 'Professional presentation style',
      icon: BusinessCenterIcon,
   },
];

// Page size configurations (in mm)
const pageSizes = {
   a4: { width: 210, height: 297, label: '📄 A4 (210×297mm)' },
   letter: { width: 216, height: 279, label: '📄 Letter (8.5×11")' },
   tabloid: { width: 279, height: 432, label: '📄 Tabloid (11×17")' },
   a3: { width: 297, height: 420, label: '📄 A3 (297×420mm)' },
   custom: { width: 300, height: 400, label: '📄 Custom Size' },
};

// Quality settings with enhanced DPI
const qualitySettings = {
   web: { dpi: 72, scale: 1, label: '🌐 Web (72 DPI)' },
   email: { dpi: 150, scale: 2, label: '📧 Email (150 DPI)' },
   print: { dpi: 300, scale: 3, label: '🖨️ Print (300 DPI)' },
   ultra: { dpi: 600, scale: 4, label: '💎 Ultra HD (600 DPI)' },
};

const MagazineExport: React.FC<MagazineExportProps> = ({
   photos,
   albumTitle,
   albumDescription,
   photoSelectionSet,
   onTogglePhotoSelection,
   onSharePhoto,
}) => {
   const [selectedTemplate, setSelectedTemplate] = useState<LayoutTemplate>('classic');
   const [exportFormat, setExportFormat] = useState<ExportFormat>('pdf');
   const [exportQuality, setExportQuality] = useState<ExportQuality>('print');
   const [pageSize, setPageSize] = useState<PageSize>('a4');
   const [pageOrientation, setPageOrientation] = useState<PageOrientation>('landscape');
   const [customWidth, setCustomWidth] = useState(300);
   const [customHeight, setCustomHeight] = useState(400);
   const [photosPerPage, setPhotosPerPage] = useState(12);
   const [enableMultiPage, setEnableMultiPage] = useState(false);
   const [customTitle, setCustomTitle] = useState(albumTitle);
   const [customSubtitle, setCustomSubtitle] = useState(albumDescription || '');
   const [isExporting, setIsExporting] = useState(false);
   const [exportProgress, setExportProgress] = useState(0);
   // Use external selection state if provided, otherwise use all photos
   const selectedPhotos = photoSelectionSet
      ? photos.filter((photo) => photoSelectionSet.has(photo.id))
      : photos;
   const [previewDialog, setPreviewDialog] = useState(false);

   const canvasRef = useRef<HTMLDivElement>(null);

   // Debug logging
   useEffect(() => {
      console.log('MagazineExport - Total photos received:', photos.length);
      console.log('MagazineExport - First few photos:', photos.slice(0, 3));
      console.log('MagazineExport - Selected photos:', selectedPhotos.length);
   }, [photos, selectedPhotos]);

   // Image loading and CORS handling
   const [imageCache, setImageCache] = useState<{ [url: string]: string }>({});
   const [imagesLoaded, setImagesLoaded] = useState(true); // Set to true for now to skip loading

   // Preload and convert images to data URLs to avoid CORS issues
   useEffect(() => {
      // Temporarily disabled for debugging
      /*
    const loadImages = async () => {
      if (selectedPhotos.length === 0) return;
      
      setImagesLoaded(false);
      const cache: { [url: string]: string } = {};
      
      try {
        await Promise.all(
          selectedPhotos.map(async (photo) => {
            try {
              // First try with CORS
              const proxyImg = new Image();
              proxyImg.crossOrigin = 'anonymous';
              
              return new Promise<void>((resolve) => {
                const timeout = setTimeout(() => {
                  console.warn('Image loading timeout:', photo.url);
                  cache[photo.url] = photo.url; // Fallback to original URL
                  resolve();
                }, 10000);
                
                proxyImg.onload = () => {
                  clearTimeout(timeout);
                  try {
                    // Convert to canvas and then to data URL
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = Math.min(proxyImg.naturalWidth, 1200); // Limit size for performance
                    canvas.height = Math.min(proxyImg.naturalHeight, 1200);
                    
                    if (ctx) {
                      // Calculate aspect ratio
                      const aspectRatio = proxyImg.naturalWidth / proxyImg.naturalHeight;
                      if (aspectRatio > 1) {
                        canvas.height = canvas.width / aspectRatio;
                      } else {
                        canvas.width = canvas.height * aspectRatio;
                      }
                      
                      ctx.drawImage(proxyImg, 0, 0, canvas.width, canvas.height);
                      cache[photo.url] = canvas.toDataURL('image/jpeg', 0.85);
                    }
                  } catch (error) {
                    console.warn('Failed to convert image to data URL:', error);
                    cache[photo.url] = photo.url; // Fallback to original URL
                  }
                  resolve();
                };
                
                proxyImg.onerror = () => {
                  clearTimeout(timeout);
                  console.warn('Failed to load image with CORS:', photo.url);
                  
                  // Try without CORS as fallback
                  const fallbackImg = new Image();
                  fallbackImg.onload = () => {
                    cache[photo.url] = photo.url; // Use original URL
                    resolve();
                  };
                  fallbackImg.onerror = () => {
                    cache[photo.url] = photo.url; // Final fallback
                    resolve();
                  };
                  fallbackImg.src = photo.url;
                };
                
                // Try loading the image
                proxyImg.src = photo.url;
              });
            } catch (error) {
              console.warn('Error loading image:', photo.url, error);
              cache[photo.url] = photo.url;
            }
          })
        );
      } catch (error) {
        console.warn('Error in image loading batch:', error);
      }
      
      setImageCache(cache);
      setImagesLoaded(true);
    };

    loadImages();
    */
   }, [selectedPhotos]);

   // Get cached image URL or fallback
   const getImageUrl = (originalUrl: string): string => {
      return originalUrl; // Simplified for now
      // return imageCache[originalUrl] || originalUrl;
   };

   const handleTemplateSelect = (template: LayoutTemplate) => {
      setSelectedTemplate(template);
   };

   // Generate different magazine layouts
   const generateLayout = useCallback((): JSX.Element => {
      // Calculate actual page dimensions for preview
      const currentPageSize =
         pageSize === 'custom' ? { width: customWidth, height: customHeight } : pageSizes[pageSize];

      const isLandscape = pageOrientation === 'landscape';
      const pageWidth = isLandscape
         ? Math.max(currentPageSize.width, currentPageSize.height)
         : Math.min(currentPageSize.width, currentPageSize.height);
      const pageHeight = isLandscape
         ? Math.min(currentPageSize.width, currentPageSize.height)
         : Math.max(currentPageSize.width, currentPageSize.height);

      // Convert mm to pixels for preview (using 3.78 as conversion factor)
      const pixelWidth = pageWidth * 3.78;
      const pixelHeight = pageHeight * 3.78;

      const baseStyle = {
         padding: '20px',
         fontFamily: '"Playfair Display", "Georgia", serif',
         backgroundColor: 'white',
         width: '100%',
         minHeight: '500px',
         boxSizing: 'border-box' as const,
         borderRadius: '8px',
         boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      };

      // Show photos per page based on multi-page setting
      const photosToShow = enableMultiPage
         ? selectedPhotos.slice(0, photosPerPage)
         : selectedPhotos;

      // Debug logging for layout
      console.log('generateLayout - Template:', selectedTemplate);
      console.log('generateLayout - photosToShow length:', photosToShow.length);
      console.log('generateLayout - photosToShow sample:', photosToShow.slice(0, 2));

      switch (selectedTemplate) {
         case 'classic':
            // Check if we have photos to display
            if (photosToShow.length === 0) {
               return (
                  <div style={baseStyle}>
                     <div
                        style={{
                           textAlign: 'center',
                           padding: '60px 20px',
                           color: '#666',
                        }}
                     >
                        <h2 style={{ margin: '0 0 16px 0', color: '#999' }}>
                           📷 No Photos Available
                        </h2>
                        <p style={{ margin: 0, fontSize: '14px' }}>
                           Please select some photos from your album to create a magazine layout.
                        </p>
                        <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#aaa' }}>
                           Total photos received: {photos.length} | Selected:{' '}
                           {selectedPhotos.length}
                        </p>
                     </div>
                  </div>
               );
            }

            return (
               <div style={baseStyle}>
                  {/* Header */}
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                     <h1
                        style={{
                           fontWeight: 700,
                           fontSize: '2rem',
                           color: '#2c3e50',
                           margin: '0 0 10px 0',
                        }}
                     >
                        {customTitle}
                     </h1>
                     {customSubtitle && (
                        <h2
                           style={{
                              color: '#7f8c8d',
                              fontWeight: 300,
                              fontSize: '1rem',
                              margin: 0,
                           }}
                        >
                           {customSubtitle}
                        </h2>
                     )}
                  </div>

                  {/* Simple Photo Grid */}
                  <div
                     style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '10px',
                        width: '100%',
                        height: 'auto',
                     }}
                  >
                     {photosToShow.map((photo, index) => (
                        <div
                           key={photo.id}
                           style={{
                              width: '100%',
                              height: '120px',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              position: 'relative',
                           }}
                        >
                           <img
                              src={photo.url}
                              alt={photo.title || `Photo ${index + 1}`}
                              style={{
                                 width: '100%',
                                 height: '100%',
                                 objectFit: 'cover',
                              }}
                              onError={(e) => {
                                 console.error(`❌ Image ${index + 1} failed to load:`, photo.url);
                                 const target = e.target as HTMLImageElement;
                                 // Replace with a simple colored div
                                 const parent = target.parentElement;
                                 if (parent) {
                                    parent.innerHTML = `
                          <div style="
                            width: 100%; 
                            height: 100%; 
                            background: linear-gradient(45deg, #e0e0e0, #f5f5f5);
                            display: flex; 
                            align-items: center; 
                            justify-content: center;
                            font-size: 12px;
                            color: #666;
                            border: 2px dashed #ccc;
                          ">
                            📷 Photo ${index + 1}
                          </div>
                        `;
                                 }
                              }}
                           />
                           <div
                              style={{
                                 position: 'absolute',
                                 top: '4px',
                                 right: '4px',
                                 backgroundColor: 'rgba(0,0,0,0.7)',
                                 color: 'white',
                                 padding: '2px 6px',
                                 borderRadius: '4px',
                                 fontSize: '10px',
                                 fontWeight: 600,
                              }}
                           >
                              {index + 1}
                           </div>
                        </div>
                     ))}
                  </div>

                  {/* Footer */}
                  <div
                     style={{
                        textAlign: 'center',
                        marginTop: '20px',
                        fontSize: '12px',
                        color: '#999',
                     }}
                  >
                     {photosToShow.length} photos • Created with Wetrippen
                  </div>
               </div>
            );

         case 'story':
            return (
               <div style={baseStyle}>
                  <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                     <h1
                        style={{
                           fontWeight: 900,
                           fontSize: '4rem',
                           background: 'linear-gradient(45deg, #2c3e50, #3498db)',
                           backgroundClip: 'text',
                           WebkitBackgroundClip: 'text',
                           WebkitTextFillColor: 'transparent',
                           letterSpacing: '-0.03em',
                           marginBottom: '16px',
                           margin: 0,
                        }}
                     >
                        {customTitle}
                     </h1>
                     {customSubtitle && (
                        <h2
                           style={{
                              color: '#34495e',
                              fontWeight: 400,
                              maxWidth: '600px',
                              margin: '0 auto',
                              fontSize: '1.5rem',
                           }}
                        >
                           {customSubtitle}
                        </h2>
                     )}
                  </div>
                  {selectedPhotos.slice(0, 6).map((photo, index) => (
                     <div
                        key={photo.id}
                        style={{
                           display: 'flex',
                           flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                           alignItems: 'center',
                           gap: '32px',
                           marginBottom: '32px',
                        }}
                     >
                        <div style={{ flex: 1 }}>
                           <img
                              src={getImageUrl(photo.url)}
                              alt={photo.title || `Photo ${index + 1}`}
                              style={{
                                 width: '100%',
                                 height: '300px',
                                 objectFit: 'cover',
                                 borderRadius: '12px',
                                 boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                              }}
                           />
                        </div>
                        <div style={{ flex: 1, textAlign: index % 2 === 0 ? 'left' : 'right' }}>
                           <h3
                              style={{
                                 fontWeight: 700,
                                 color: '#2c3e50',
                                 marginBottom: '16px',
                                 fontSize: '2rem',
                              }}
                           >
                              {photo.title || `Chapter ${index + 1}`}
                           </h3>
                           <p
                              style={{
                                 color: '#7f8c8d',
                                 lineHeight: 1.8,
                                 fontSize: '1.1rem',
                              }}
                           >
                              A beautiful moment captured in time. Each photo tells a unique story
                              and holds precious memories that deserve to be cherished forever.
                           </p>
                        </div>
                     </div>
                  ))}
               </div>
            );

         case 'minimal':
            return (
               <div style={baseStyle}>
                  <h1
                     style={{
                        fontWeight: 100,
                        fontSize: '5rem',
                        color: '#2c3e50',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        marginBottom: '16px',
                        margin: 0,
                     }}
                  >
                     {customTitle}
                  </h1>
                  {customSubtitle && (
                     <h2
                        style={{
                           color: '#95a5a6',
                           fontWeight: 300,
                           textAlign: 'center',
                           marginBottom: '64px',
                           maxWidth: '500px',
                           fontSize: '1.5rem',
                           margin: '0 auto 64px auto',
                        }}
                     >
                        {customSubtitle}
                     </h2>
                  )}
                  <div
                     style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '8px',
                        width: '100%',
                        maxWidth: '800px',
                        margin: '0 auto',
                     }}
                  >
                     {selectedPhotos.slice(0, 9).map((photo, index) => (
                        <img
                           key={photo.id}
                           src={getImageUrl(photo.url)}
                           alt={photo.title || `Photo ${index + 1}`}
                           style={{
                              width: '100%',
                              height: '200px',
                              objectFit: 'cover',
                           }}
                        />
                     ))}
                  </div>
               </div>
            );

         case 'collage':
            return (
               <div style={baseStyle}>
                  <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                     <h1
                        style={{
                           fontWeight: 800,
                           fontSize: '3.5rem',
                           color: '#2c3e50',
                           marginBottom: '16px',
                           margin: 0,
                        }}
                     >
                        {customTitle}
                     </h1>
                     {customSubtitle && (
                        <h2
                           style={{
                              color: '#7f8c8d',
                              fontStyle: 'italic',
                              fontWeight: 300,
                              fontSize: '1.5rem',
                              margin: 0,
                           }}
                        >
                           {customSubtitle}
                        </h2>
                     )}
                  </div>
                  <div
                     style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '16px',
                        gridAutoRows: '200px',
                     }}
                  >
                     {selectedPhotos.map((photo, index) => (
                        <div
                           key={photo.id}
                           style={{
                              gridColumn: index === 0 ? 'span 2' : 'span 1',
                              gridRow: index === 0 ? 'span 2' : 'span 1',
                              transform: `rotate(${(index % 2 === 0 ? 1 : -1) * (Math.random() * 6 - 3)}deg)`,
                              transition: 'transform 0.3s ease',
                           }}
                        >
                           <img
                              src={getImageUrl(photo.url)}
                              alt={photo.title || `Photo ${index + 1}`}
                              style={{
                                 width: '100%',
                                 height: '100%',
                                 objectFit: 'cover',
                                 borderRadius: '8px',
                                 boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                              }}
                           />
                        </div>
                     ))}
                  </div>
               </div>
            );

         case 'travel':
            return (
               <div style={baseStyle}>
                  <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                     <h1
                        style={{
                           fontWeight: 800,
                           fontSize: '3rem',
                           color: '#2c3e50',
                           marginBottom: '8px',
                           margin: 0,
                        }}
                     >
                        🌍 {customTitle}
                     </h1>
                     {customSubtitle && (
                        <h2
                           style={{
                              color: '#7f8c8d',
                              fontStyle: 'italic',
                              fontWeight: 300,
                              fontSize: '1.5rem',
                              margin: 0,
                           }}
                        >
                           {customSubtitle}
                        </h2>
                     )}
                  </div>
                  <div
                     style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        gap: '24px',
                     }}
                  >
                     <div
                        style={{
                           display: 'grid',
                           gridTemplateColumns: 'repeat(2, 1fr)',
                           gap: '16px',
                        }}
                     >
                        {selectedPhotos.slice(0, 8).map((photo, index) => (
                           <div key={photo.id} style={{ position: 'relative' }}>
                              <img
                                 src={getImageUrl(photo.url)}
                                 alt={photo.title || `Photo ${index + 1}`}
                                 style={{
                                    width: '100%',
                                    height: '200px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                 }}
                              />
                              {photo.location && (
                                 <div
                                    style={{
                                       position: 'absolute',
                                       top: '8px',
                                       right: '8px',
                                       backgroundColor: 'rgba(255,255,255,0.9)',
                                       color: '#2c3e50',
                                       padding: '4px 8px',
                                       borderRadius: '4px',
                                       fontSize: '0.75rem',
                                       fontWeight: 600,
                                    }}
                                 >
                                    📍 Location
                                 </div>
                              )}
                           </div>
                        ))}
                     </div>
                     <div
                        style={{
                           backgroundColor: '#f8f9fa',
                           padding: '20px',
                           borderRadius: '8px',
                           display: 'flex',
                           flexDirection: 'column',
                           justifyContent: 'center',
                           alignItems: 'center',
                        }}
                     >
                        <h3 style={{ color: '#2c3e50', textAlign: 'center', marginBottom: '16px' }}>
                           Travel Highlights
                        </h3>
                        <p style={{ color: '#7f8c8d', textAlign: 'center', lineHeight: 1.6 }}>
                           Discover amazing places and create unforgettable memories through these
                           beautiful captured moments.
                        </p>
                     </div>
                  </div>
               </div>
            );

         case 'portfolio':
            return (
               <div style={baseStyle}>
                  <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                     <h1
                        style={{
                           fontWeight: 300,
                           fontSize: '2.5rem',
                           color: '#2c3e50',
                           letterSpacing: '0.1em',
                           textTransform: 'uppercase',
                           marginBottom: '8px',
                           margin: 0,
                        }}
                     >
                        {customTitle}
                     </h1>
                     {customSubtitle && (
                        <h2
                           style={{
                              color: '#7f8c8d',
                              fontWeight: 400,
                              fontSize: '1.2rem',
                              margin: 0,
                           }}
                        >
                           {customSubtitle}
                        </h2>
                     )}
                  </div>
                  <div
                     style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '8px',
                     }}
                  >
                     {selectedPhotos.map((photo, index) => (
                        <img
                           key={photo.id}
                           src={getImageUrl(photo.url)}
                           alt={photo.title || `Photo ${index + 1}`}
                           style={{
                              width: '100%',
                              height: index % 3 === 0 ? '300px' : '200px',
                              objectFit: 'cover',
                           }}
                        />
                     ))}
                  </div>
               </div>
            );

         default:
            return generateLayout();
      }
   }, [
      selectedTemplate,
      customTitle,
      customSubtitle,
      selectedPhotos,
      pageSize,
      pageOrientation,
      customWidth,
      customHeight,
      photosPerPage,
      enableMultiPage,
      exportQuality,
      qualitySettings,
      pageSizes,
      imagesLoaded,
   ]);

   const handleExport = async () => {
      if (!canvasRef.current) return;

      setIsExporting(true);
      setExportProgress(0);

      try {
         const progressInterval = setInterval(() => {
            setExportProgress((prev) => Math.min(prev + 5, 85));
         }, 200);

         // Get page dimensions
         const currentPageSize =
            pageSize === 'custom'
               ? { width: customWidth, height: customHeight }
               : pageSizes[pageSize];

         const isLandscape = pageOrientation === 'landscape';
         const pageWidth = isLandscape
            ? Math.max(currentPageSize.width, currentPageSize.height)
            : Math.min(currentPageSize.width, currentPageSize.height);
         const pageHeight = isLandscape
            ? Math.min(currentPageSize.width, currentPageSize.height)
            : Math.max(currentPageSize.width, currentPageSize.height);

         // Get quality settings
         const quality = qualitySettings[exportQuality];

         if (exportFormat === 'svg') {
            // SVG Export
            await exportAsSVG(canvasRef.current, pageWidth, pageHeight);
         } else if (enableMultiPage && exportFormat === 'pdf') {
            // Multi-page PDF Export
            await exportAsMultiPagePDF(pageWidth, pageHeight, quality);
         } else {
            // Single page export (PDF, PNG, JPG)
            const canvas = await html2canvas(canvasRef.current, {
               scale: quality.scale,
               useCORS: true,
               allowTaint: false,
               backgroundColor: '#ffffff',
               width: canvasRef.current.scrollWidth,
               height: canvasRef.current.scrollHeight,
               logging: false,
               imageTimeout: 15000,
               removeContainer: true,
               foreignObjectRendering: false,
               onclone: (clonedDoc, element) => {
                  // Ensure all images are loaded in the cloned document
                  const images = element.querySelectorAll('img');
                  images.forEach((img: HTMLImageElement) => {
                     if (img.src && imageCache[img.getAttribute('data-original-src') || img.src]) {
                        img.src = imageCache[img.getAttribute('data-original-src') || img.src];
                     }
                  });
               },
            });

            clearInterval(progressInterval);
            setExportProgress(95);

            if (exportFormat === 'pdf') {
               const pdf = new jsPDF({
                  orientation: pageOrientation,
                  unit: 'mm',
                  format: [pageWidth, pageHeight],
               });

               const imgData = canvas.toDataURL('image/jpeg', 0.95);
               const pdfWidth = pdf.internal.pageSize.getWidth();
               const pdfHeight = pdf.internal.pageSize.getHeight();

               // Calculate aspect ratio to fit image properly
               const canvasRatio = canvas.width / canvas.height;
               const pdfRatio = pdfWidth / pdfHeight;

               let finalWidth = pdfWidth;
               let finalHeight = pdfHeight;
               let offsetX = 0;
               let offsetY = 0;

               if (canvasRatio > pdfRatio) {
                  finalHeight = pdfWidth / canvasRatio;
                  offsetY = (pdfHeight - finalHeight) / 2;
               } else {
                  finalWidth = pdfHeight * canvasRatio;
                  offsetX = (pdfWidth - finalWidth) / 2;
               }

               pdf.addImage(imgData, 'JPEG', offsetX, offsetY, finalWidth, finalHeight);
               pdf.save(`${customTitle.replace(/\s+/g, '_')}_magazine_${quality.dpi}dpi.pdf`);
            } else {
               // PNG/JPG Export
               const link = document.createElement('a');
               link.download = `${customTitle.replace(/\s+/g, '_')}_magazine_${quality.dpi}dpi.${exportFormat}`;
               link.href = canvas.toDataURL(`image/${exportFormat}`, 0.95);
               link.click();
            }
         }

         clearInterval(progressInterval);
         setExportProgress(100);
      } catch (error) {
         console.error('Export failed:', error);
      } finally {
         setTimeout(() => {
            setIsExporting(false);
            setExportProgress(0);
         }, 1000);
      }
   };

   // SVG Export Function
   const exportAsSVG = async (element: HTMLDivElement, width: number, height: number) => {
      const svgContent = await generateSVGContent(element, width, height);
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.download = `${customTitle.replace(/\s+/g, '_')}_magazine.svg`;
      link.href = url;
      link.click();

      URL.revokeObjectURL(url);
   };

   // Generate SVG Content
   const generateSVGContent = async (
      element: HTMLDivElement,
      width: number,
      height: number
   ): Promise<string> => {
      // Convert mm to pixels (assuming 96 DPI)
      const pixelWidth = (width * 96) / 25.4;
      const pixelHeight = (height * 96) / 25.4;

      return `
      <svg width="${pixelWidth}" height="${pixelHeight}" xmlns="http://www.w3.org/2000/svg">
        <foreignObject width="100%" height="100%">
          <div xmlns="http://www.w3.org/1999/xhtml" style="font-family: 'Playfair Display', serif;">
            ${element.innerHTML}
          </div>
        </foreignObject>
      </svg>
    `;
   };

   // Multi-page PDF Export Function
   const exportAsMultiPagePDF = async (
      pageWidth: number,
      pageHeight: number,
      quality: typeof qualitySettings.print
   ) => {
      const pdf = new jsPDF({
         orientation: pageOrientation,
         unit: 'mm',
         format: [pageWidth, pageHeight],
      });

      const totalPages = Math.ceil(selectedPhotos.length / photosPerPage);

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
         const startIndex = pageIndex * photosPerPage;
         const endIndex = Math.min(startIndex + photosPerPage, selectedPhotos.length);
         const pagePhotos = selectedPhotos.slice(startIndex, endIndex);

         // Create a temporary div for this page
         const pageElement = document.createElement('div');
         pageElement.style.cssText = `
        padding: 24px;
        font-family: "Playfair Display", "Georgia", serif;
        background-color: white;
        width: ${pageWidth * 3.78}px;
        height: ${pageHeight * 3.78}px;
        box-sizing: border-box;
      `;

         // Add page header
         pageElement.innerHTML = `
        <div style="text-align: center; margin-bottom: 32px;">
          <h1 style="font-weight: 800; font-size: 3rem; color: #2c3e50; margin: 0 0 8px 0;">
            ${customTitle}
          </h1>
          ${customSubtitle ? `<h2 style="color: #7f8c8d; font-style: italic; font-weight: 300; font-size: 1.5rem; margin: 0;">${customSubtitle}</h2>` : ''}
          <p style="color: #95a5a6; font-size: 1rem; margin: 16px 0 0 0;">Page ${pageIndex + 1} of ${totalPages}</p>
        </div>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px;">
          ${pagePhotos
             .map(
                (photo, index) => `
            <div style="position: relative;">
              <img src="${getImageUrl(photo.url)}" alt="${photo.title || `Photo ${index + 1}`}" 
                   style="width: 100%; height: 250px; object-fit: cover; border-radius: 8px;" />
              ${
                 photo.title
                    ? `
                <div style="position: absolute; bottom: 8px; left: 8px; background-color: rgba(0,0,0,0.7); 
                           color: white; padding: 4px 8px; border-radius: 4px; font-weight: 600; font-size: 0.875rem;">
                  ${photo.title}
                </div>
              `
                    : ''
              }
            </div>
          `
             )
             .join('')}
        </div>
      `;

         document.body.appendChild(pageElement);

         // Capture this page
         const canvas = await html2canvas(pageElement, {
            scale: quality.scale,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
         });

         // Add page to PDF
         if (pageIndex > 0) {
            pdf.addPage();
         }

         const imgData = canvas.toDataURL('image/jpeg', 0.95);
         const pdfWidth = pdf.internal.pageSize.getWidth();
         const pdfHeight = pdf.internal.pageSize.getHeight();

         pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);

         // Clean up
         document.body.removeChild(pageElement);

         // Update progress
         setExportProgress(85 + (pageIndex / totalPages) * 10);
      }

      pdf.save(
         `${customTitle.replace(/\s+/g, '_')}_magazine_${totalPages}pages_${quality.dpi}dpi.pdf`
      );
   };

   const handleEmailExport = () => {
      const subject = encodeURIComponent(`Photo Album: ${customTitle}`);
      const body = encodeURIComponent(
         `Check out this photo album: ${customTitle}\n\n${customSubtitle}`
      );
      window.open(`mailto:?subject=${subject}&body=${body}`);
   };

   return (
      <ExportContainer>
         {/* Debug Info */}

         <Box sx={{ mb: 4 }}>
            <Typography
               variant='h5'
               sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
            >
               <ArticleIcon color='primary' />
               Magazine Export
            </Typography>
            <Typography variant='body2' color='text.secondary'>
               Create beautiful magazine-style layouts from your album photos
            </Typography>
         </Box>

         {/* Photo Selection Section */}
         {photoSelectionSet && onTogglePhotoSelection && (
            <Box sx={{ mb: 4 }}>
               <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                  Select Photos for Magazine ({selectedPhotos.length} of {photos.length} selected)
               </Typography>
               <Grid container spacing={2}>
                  {photos.map((photo) => (
                     <Grid item xs={6} sm={4} md={3} lg={2} key={photo.id}>
                        <Card
                           sx={{
                              position: 'relative',
                              cursor: 'pointer',
                              border: photoSelectionSet.has(photo.id)
                                 ? '3px solid #1976d2'
                                 : '1px solid #e0e0e0',
                              '&:hover': {
                                 boxShadow: 3,
                              },
                           }}
                           onClick={() => onTogglePhotoSelection(photo.id)}
                        >
                           <CardMedia
                              component='img'
                              height='120'
                              image={photo.url}
                              alt={photo.title || 'Photo'}
                              sx={{
                                 objectFit: 'cover',
                                 opacity: photoSelectionSet.has(photo.id) ? 1 : 0.6,
                              }}
                           />

                           {/* Selection checkbox overlay */}
                           <Box
                              sx={{
                                 position: 'absolute',
                                 top: 8,
                                 right: 8,
                                 bgcolor: photoSelectionSet.has(photo.id)
                                    ? '#1976d2'
                                    : 'rgba(0,0,0,0.6)',
                                 borderRadius: '50%',
                                 p: 0.3,
                                 border: photoSelectionSet.has(photo.id)
                                    ? '2px solid white'
                                    : '2px solid transparent',
                              }}
                           >
                              <Checkbox
                                 checked={photoSelectionSet.has(photo.id)}
                                 size='small'
                                 sx={{
                                    color: 'white',
                                    p: 0,
                                    '&.Mui-checked': {
                                       color: 'white',
                                    },
                                 }}
                                 onClick={(e) => e.stopPropagation()}
                              />
                           </Box>

                           {/* Share button */}
                           {onSharePhoto && (
                              <Box
                                 sx={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    zIndex: 10,
                                 }}
                              >
                                 <IconButton
                                    sx={{
                                       bgcolor: 'rgba(0,0,0,0.6)',
                                       color: 'white',
                                       width: 28,
                                       height: 28,
                                       '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                                    }}
                                    size='small'
                                    onClick={(e) => {
                                       e.stopPropagation();
                                       onSharePhoto(photo);
                                    }}
                                    title='Share photo'
                                 >
                                    <ShareIcon fontSize='small' />
                                 </IconButton>
                              </Box>
                           )}
                        </Card>
                     </Grid>
                  ))}
               </Grid>
            </Box>
         )}

         {/* Template Selection */}
         <Box sx={{ mb: 4 }}>
            <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
               Choose Layout Template
            </Typography>
            <Grid container spacing={2}>
               {templates.map((template) => {
                  const IconComponent = template.icon;
                  return (
                     <Grid item xs={12} sm={6} md={4} key={template.id}>
                        <TemplateCard
                           selected={selectedTemplate === template.id}
                           onClick={() => handleTemplateSelect(template.id)}
                        >
                           <CardContent sx={{ textAlign: 'center', p: 2 }}>
                              <IconComponent sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                              <Typography variant='subtitle1' fontWeight={600}>
                                 {template.name}
                              </Typography>
                              <Typography variant='caption' color='text.secondary'>
                                 {template.description}
                              </Typography>
                           </CardContent>
                        </TemplateCard>
                     </Grid>
                  );
               })}
            </Grid>
         </Box>

         {/* Customization Options */}
         <Box sx={{ mb: 4 }}>
            <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
               Customize Content
            </Typography>
            <Grid container spacing={2}>
               <Grid item xs={12} md={6}>
                  <TextField
                     fullWidth
                     label='Album Title'
                     value={customTitle}
                     onChange={(e) => setCustomTitle(e.target.value)}
                     variant='outlined'
                  />
               </Grid>
               <Grid item xs={12} md={6}>
                  <TextField
                     fullWidth
                     label='Subtitle/Description'
                     value={customSubtitle}
                     onChange={(e) => setCustomSubtitle(e.target.value)}
                     variant='outlined'
                  />
               </Grid>
            </Grid>
         </Box>

         {/* Export Settings - Hidden */}
         <Box sx={{ mb: 4, display: 'none' }}>
            <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
               Export Settings
            </Typography>
            <Grid container spacing={2}>
               <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                     <InputLabel>Format</InputLabel>
                     <Select
                        value={exportFormat}
                        label='Format'
                        onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                     >
                        <MenuItem value='pdf'>📄 PDF Document</MenuItem>
                        <MenuItem value='png'>🖼️ PNG Image</MenuItem>
                        <MenuItem value='jpg'>📷 JPG Image</MenuItem>
                        <MenuItem value='svg'>🖼️ SVG Vector</MenuItem>
                     </Select>
                  </FormControl>
               </Grid>
               <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                     <InputLabel>Quality</InputLabel>
                     <Select
                        value={exportQuality}
                        label='Quality'
                        onChange={(e) => setExportQuality(e.target.value as ExportQuality)}
                     >
                        {Object.entries(qualitySettings).map(([key, setting]) => (
                           <MenuItem key={key} value={key}>
                              {setting.label}
                           </MenuItem>
                        ))}
                     </Select>
                  </FormControl>
               </Grid>
               <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                     <InputLabel>Page Size</InputLabel>
                     <Select
                        value={pageSize}
                        label='Page Size'
                        onChange={(e) => setPageSize(e.target.value as PageSize)}
                     >
                        {Object.entries(pageSizes).map(([key, size]) => (
                           <MenuItem key={key} value={key}>
                              {size.label}
                           </MenuItem>
                        ))}
                     </Select>
                  </FormControl>
               </Grid>
               <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                     <InputLabel>Orientation</InputLabel>
                     <Select
                        value={pageOrientation}
                        label='Orientation'
                        onChange={(e) => setPageOrientation(e.target.value as PageOrientation)}
                     >
                        <MenuItem value='landscape'>🖼️ Landscape</MenuItem>
                        <MenuItem value='portrait'>📱 Portrait</MenuItem>
                     </Select>
                  </FormControl>
               </Grid>

               {/* Custom Size Controls */}
               {pageSize === 'custom' && (
                  <>
                     <Grid item xs={6} md={3}>
                        <TextField
                           fullWidth
                           label='Width (mm)'
                           type='number'
                           value={customWidth}
                           onChange={(e) => setCustomWidth(Number(e.target.value))}
                           inputProps={{ min: 100, max: 1000 }}
                        />
                     </Grid>
                     <Grid item xs={6} md={3}>
                        <TextField
                           fullWidth
                           label='Height (mm)'
                           type='number'
                           value={customHeight}
                           onChange={(e) => setCustomHeight(Number(e.target.value))}
                           inputProps={{ min: 100, max: 1000 }}
                        />
                     </Grid>
                  </>
               )}

               {/* Multi-page Controls */}
               <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                     <ToggleButton
                        value='multipage'
                        selected={enableMultiPage}
                        onChange={() => setEnableMultiPage(!enableMultiPage)}
                        size='small'
                        sx={{
                           px: 2,
                           '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                 bgcolor: 'primary.dark',
                              },
                           },
                        }}
                     >
                        📚 Multi-page PDF
                     </ToggleButton>

                     {enableMultiPage && (
                        <FormControl size='small' sx={{ minWidth: 180 }}>
                           <InputLabel>Photos per page</InputLabel>
                           <Select
                              value={photosPerPage}
                              label='Photos per page'
                              onChange={(e) => setPhotosPerPage(Number(e.target.value))}
                           >
                              <MenuItem value={4}>4 photos per page</MenuItem>
                              <MenuItem value={6}>6 photos per page</MenuItem>
                              <MenuItem value={9}>9 photos per page</MenuItem>
                              <MenuItem value={12}>12 photos per page</MenuItem>
                              <MenuItem value={16}>16 photos per page</MenuItem>
                           </Select>
                        </FormControl>
                     )}

                     <Typography variant='body2' color='text.secondary'>
                        {enableMultiPage
                           ? `${Math.ceil(selectedPhotos.length / photosPerPage)} pages • ${selectedPhotos.length} photos`
                           : `${selectedPhotos.length} photos selected`}
                     </Typography>
                  </Box>
               </Grid>
            </Grid>
         </Box>

         {/* Export Quality Indicator - Hidden */}
         <Box sx={{ mb: 4, display: 'none' }}>
            <Paper
               sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}
            >
               <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}
               >
                  📊 Export Preview
               </Typography>
               <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                     <Typography variant='body2' color='text.secondary'>
                        Format
                     </Typography>
                     <Typography variant='body1' fontWeight={600}>
                        {exportFormat.toUpperCase()}
                        {exportFormat === 'svg' && ' (Vector)'}
                     </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                     <Typography variant='body2' color='text.secondary'>
                        Quality
                     </Typography>
                     <Typography variant='body1' fontWeight={600}>
                        {qualitySettings[exportQuality].dpi} DPI
                     </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                     <Typography variant='body2' color='text.secondary'>
                        Page Size
                     </Typography>
                     <Typography variant='body1' fontWeight={600}>
                        {pageSize === 'custom'
                           ? `${customWidth}×${customHeight}mm`
                           : `${pageSizes[pageSize].label.split(' ')[1]}`}{' '}
                        ({pageOrientation})
                     </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                     <Typography variant='body2' color='text.secondary'>
                        Output
                     </Typography>
                     <Typography variant='body1' fontWeight={600}>
                        {enableMultiPage
                           ? `${Math.ceil(selectedPhotos.length / photosPerPage)} pages`
                           : '1 page'}
                     </Typography>
                  </Grid>
               </Grid>
            </Paper>
         </Box>

         {/* Preview */}
         <Box sx={{ mb: 4 }}>
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
               }}
            >
               <Typography variant='h6' sx={{ fontWeight: 600 }}>
                  Preview
               </Typography>
               <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {!imagesLoaded && selectedPhotos.length > 0 && (
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                        <CircularProgress size={16} />
                        <Typography variant='caption' color='text.secondary'>
                           Loading images...
                        </Typography>
                     </Box>
                  )}
                  <Button
                     variant='outlined'
                     onClick={() => setPreviewDialog(true)}
                     startIcon={<PaletteIcon />}
                     disabled={!imagesLoaded && selectedPhotos.length > 0}
                  >
                     Full Preview
                  </Button>
               </Box>
            </Box>

            {!imagesLoaded && selectedPhotos.length > 0 ? (
               <Box
                  sx={{
                     minHeight: '400px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     backgroundColor: 'grey.50',
                     borderRadius: 2,
                     border: '2px dashed',
                     borderColor: 'grey.300',
                  }}
               >
                  <Box sx={{ textAlign: 'center' }}>
                     <CircularProgress sx={{ mb: 2 }} />
                     <Typography variant='h6' color='text.secondary' gutterBottom>
                        Preparing Preview
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        Processing {selectedPhotos.length} images for high-quality export...
                     </Typography>
                  </Box>
               </Box>
            ) : (
               <PreviewCanvas>
                  <div ref={canvasRef}>{generateLayout()}</div>
               </PreviewCanvas>
            )}
         </Box>

         {/* Export Actions - Hidden */}
         <Box sx={{ display: 'none', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
               variant='contained'
               size='large'
               onClick={handleExport}
               disabled={isExporting || selectedPhotos.length === 0 || !imagesLoaded}
               startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
               sx={{ minWidth: 200 }}
            >
               {isExporting
                  ? 'Exporting...'
                  : !imagesLoaded && selectedPhotos.length > 0
                    ? 'Loading Images...'
                    : `Export ${exportFormat.toUpperCase()} ${enableMultiPage ? `(${Math.ceil(selectedPhotos.length / photosPerPage)} pages)` : ''}`}
            </Button>
            <Button
               variant='outlined'
               size='large'
               onClick={handleEmailExport}
               startIcon={<EmailIcon />}
               disabled={isExporting}
            >
               Share via Email
            </Button>
            <Button
               variant='outlined'
               size='large'
               onClick={() => window.print()}
               startIcon={<PrintIcon />}
               disabled={isExporting}
            >
               Print Preview
            </Button>
         </Box>

         {/* Export Progress */}
         {isExporting && (
            <Box sx={{ mt: 3 }}>
               <LinearProgress
                  variant='determinate'
                  value={exportProgress}
                  sx={{
                     height: 8,
                     borderRadius: 4,
                     '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                     },
                  }}
               />
               <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <Typography variant='body2' color='text.secondary'>
                     {exportProgress < 20 && '🔄 Preparing export...'}
                     {exportProgress >= 20 && exportProgress < 50 && '📸 Capturing layout...'}
                     {exportProgress >= 50 &&
                        exportProgress < 85 &&
                        '🎨 Rendering at high quality...'}
                     {exportProgress >= 85 && exportProgress < 95 && '📄 Generating file...'}
                     {exportProgress >= 95 && '✅ Almost done!'}
                  </Typography>
                  <Typography variant='caption' color='primary.main' sx={{ fontWeight: 600 }}>
                     {exportProgress}% • {qualitySettings[exportQuality].dpi} DPI •{' '}
                     {exportFormat.toUpperCase()}
                     {enableMultiPage &&
                        ` • ${Math.ceil(selectedPhotos.length / photosPerPage)} pages`}
                  </Typography>
               </Box>
            </Box>
         )}

         {/* Full Preview Dialog */}
         <Dialog
            open={previewDialog}
            onClose={() => setPreviewDialog(false)}
            maxWidth='lg'
            fullWidth
         >
            <DialogTitle>Full Preview</DialogTitle>
            <DialogContent>
               <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 1 }}>
                  {generateLayout()}
               </Box>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setPreviewDialog(false)}>Close</Button>
               <Button variant='contained' onClick={handleExport}>
                  Export This Layout
               </Button>
            </DialogActions>
         </Dialog>
      </ExportContainer>
   );
};

export default MagazineExport;
